import { optimizelyClientInstance } from '../../context/OptimizelyContext'
import { EventName } from '../types'

const nodeEnv = typeof process !== 'undefined' && process.env ? process.env.NODE_ENV : 'production'

// Admin advisor previews must not send conversion events into live
// experiments — unlike Mixpanel there is no preview tag to filter them out
// later. Set once at bootstrap, before React mounts.
let previewMode = false

export const setOptimizelyPreviewMode = (isPreview: boolean): void => {
  previewMode = isPreview
}

/**
 * Track an event in Optimizely for experimentation and analytics
 *
 * @param eventName - The name of the event to track
 * @param properties - Additional event properties/tags
 *
 * @example
 * ```ts
 * import { trackOptimizelyEvent } from '../utils/tracking/optimizely'
 *
 * const handleClick = () => {
 *   trackOptimizelyEvent('appointment-picker-opened', {
 *     advisor_id: advisorId,
 *     network_id: networkId
 *   })
 * }
 * ```
 */
export const trackOptimizelyEvent = (
  eventName: EventName,
  properties: Record<string, string | number | boolean | null | undefined> = {}
): void => {
  if (previewMode) {
    if (nodeEnv === 'development') {
      console.log(`[Optimizely] Preview mode — event "${eventName}" not tracked.`, properties)
    }
    return
  }

  if (!optimizelyClientInstance) {
    if (nodeEnv === 'development') {
      console.warn(
        `Optimizely client not initialized. Event "${eventName}" not tracked.`,
        properties
      )
    }
    return
  }

  const payload = {
    $opt_event_properties: properties
  }

  try {
    optimizelyClientInstance.track(eventName, undefined, payload)

    if (nodeEnv === 'development') {
      console.log(
        `[Optimizely] Tracked event: "${eventName}"`,
        properties
      )
    }
  } catch (error) {
    if (nodeEnv === 'development') {
      console.error(
        `Failed to track Optimizely event "${eventName}":`,
        error
      )
    }
  }
}
